import { ReactNode, useRef, useEffect, useState } from 'react';
import { WidgetModalContext } from '../context/widget-modal-context';

export function WidgetModalProvider({
  children,
  elementId,
}: {
  children: ReactNode;
  elementId?: string;
}) {
  const portalRef = useRef<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 100;

    const findPortalContainer = () => {
      // Try default container first
      const defaultRoot = document.getElementById('conversations-widget-root');
      const tryFromRoot = (rootEl: HTMLElement | null) => {
        if (rootEl?.shadowRoot) {
          const container = rootEl.shadowRoot.getElementById(
            elementId ? elementId : 'chakra-portal-container',
          );
          if (container) {
            portalRef.current = container;
            setIsReady(true);
            return true;
          }
        }
        return false;
      };

      if (tryFromRoot(defaultRoot)) return true;

      // If embedding into a provided elementId, its shadowRoot hosts the portal
      if (elementId) {
        const host = document.getElementById(elementId);
        if (tryFromRoot(host)) return true;
      }

      return false;
    };

    const tryFindContainer = () => {
      if (findPortalContainer()) {
        return;
      }

      retryCount++;
      if (retryCount < maxRetries) {
        setTimeout(tryFindContainer, retryDelay);
      } else {
        console.warn(
          'WidgetModalProvider: Could not find chakra-portal-container after',
          maxRetries,
          'attempts',
        );
      }
    };

    tryFindContainer();
  }, [elementId]);

  return (
    <WidgetModalContext.Provider value={{ portalRef, isReady }}>
      {children}
    </WidgetModalContext.Provider>
  );
}
